import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSiteConfig } from '../../hooks/useSupabase';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiAlertCircle, FiSettings, FiLock, FiUnlock, FiEye, FiEyeOff } = FiIcons;

const MaintenanceSettings = () => {
  const { data: siteConfigData, loading: configLoading } = useSiteConfig();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    maintenance_mode: false,
    maintenance_passcode: 'admin123',
    maintenance_message: 'Η ιστοσελίδα μας βρίσκεται προσωρινά υπό συντήρηση για βελτιώσεις. Θα είμαστε σύντομα πίσω!'
  });

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasscode, setShowPasscode] = useState(false);

  useEffect(() => {
    if (!configLoading && siteConfigData && siteConfigData.length > 0) {
      const config = siteConfigData[0];
      setFormData({
        maintenance_mode: config.maintenance_mode || false,
        maintenance_passcode: config.maintenance_passcode || 'admin123',
        maintenance_message: config.maintenance_message || 'Η ιστοσελίδα μας βρίσκεται προσωρινά υπό συντήρηση για βελτιώσεις. Θα είμαστε σύντομα πίσω!'
      });
    }
  }, [siteConfigData, configLoading]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Validate passcode
      if (formData.maintenance_passcode.length < 6) {
        throw new Error('Ο κωδικός πρόσβασης πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      }

      // Get the existing config
      const existingConfig = siteConfigData?.[0] || {};
      
      // Update the site config
      const updatedData = {
        ...existingConfig,
        maintenance_mode: formData.maintenance_mode,
        maintenance_passcode: formData.maintenance_passcode,
        maintenance_message: formData.maintenance_message,
        updated_at: new Date().toISOString()
      };

      // Update in Supabase
      const { error } = await supabase
        .from('site_config_aox1234')
        .update(updatedData)
        .eq('id', existingConfig.id);

      if (error) throw error;

      // Update localStorage as backup
      const localConfig = JSON.parse(localStorage.getItem('aox_site_config') || '{}');
      localStorage.setItem('aox_site_config', JSON.stringify({
        ...localConfig,
        ...updatedData
      }));

      // Clear maintenance bypass if maintenance mode is disabled
      if (!formData.maintenance_mode) {
        localStorage.removeItem('maintenance_bypass');
      }

      setMessage({
        type: 'success',
        text: formData.maintenance_mode 
          ? 'Η λειτουργία συντήρησης ενεργοποιήθηκε επιτυχώς!' 
          : 'Η λειτουργία συντήρησης απενεργοποιήθηκε επιτυχώς!'
      });

      // If maintenance mode was just enabled, show warning
      if (formData.maintenance_mode) {
        setTimeout(() => {
          setMessage({
            type: 'warning',
            text: `Η ιστοσελίδα είναι τώρα υπό συντήρηση. Κωδικός ξεκλειδώματος: ${formData.maintenance_passcode}`
          });
        }, 2000);
      }

    } catch (error) {
      console.error('Error saving maintenance settings:', error);
      setMessage({
        type: 'error',
        text: `Σφάλμα: ${error.message || 'Προέκυψε πρόβλημα κατά την αποθήκευση'}`
      });
    } finally {
      setSaving(false);
    }
  };

  const generateRandomPasscode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, maintenance_passcode: result }));
  };

  if (configLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Λειτουργία Συντήρησης</h1>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md ${
                saving ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
              {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-md flex items-center ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : message.type === 'warning'
                ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {message.text}
          </motion.div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              formData.maintenance_mode ? 'bg-yellow-100' : 'bg-green-100'
            }`}>
              <SafeIcon 
                icon={formData.maintenance_mode ? FiLock : FiUnlock} 
                className={`w-6 h-6 ${formData.maintenance_mode ? 'text-yellow-600' : 'text-green-600'}`} 
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Ρυθμίσεις Συντήρησης</h2>
              <p className="text-sm text-gray-600">
                Διαχειριστείτε τη λειτουργία συντήρησης της ιστοσελίδας
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Maintenance Mode Toggle */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Κατάσταση Συντήρησης
                  </h3>
                  <p className="text-sm text-gray-600">
                    {formData.maintenance_mode 
                      ? 'Η ιστοσελίδα είναι προς το παρόν υπό συντήρηση. Μόνο οι διαχειριστές με τον σωστό κωδικό μπορούν να την προσπελάσουν.'
                      : 'Η ιστοσελίδα είναι διαθέσιμη στο κοινό. Ενεργοποιήστε τη λειτουργία συντήρησης για να περιορίσετε την πρόσβαση.'
                    }
                  </p>
                </div>
                <div className="ml-6">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="maintenance_mode"
                      checked={formData.maintenance_mode}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-14 h-8 rounded-full transition-colors ${
                      formData.maintenance_mode ? 'bg-yellow-500' : 'bg-gray-300'
                    }`}>
                      <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                        formData.maintenance_mode ? 'translate-x-7' : 'translate-x-1'
                      } mt-1`}></div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Passcode Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Κωδικός Πρόσβασης</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="maintenance_passcode" className="block text-sm font-medium text-gray-700 mb-1">
                    Κωδικός Ξεκλειδώματος
                  </label>
                  <div className="relative">
                    <input
                      type={showPasscode ? 'text' : 'password'}
                      id="maintenance_passcode"
                      name="maintenance_passcode"
                      value={formData.maintenance_passcode}
                      onChange={handleInputChange}
                      className="block w-full pr-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Εισάγετε κωδικό..."
                      required
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-2">
                      <button
                        type="button"
                        onClick={() => setShowPasscode(!showPasscode)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={showPasscode ? 'Απόκρυψη' : 'Εμφάνιση'}
                      >
                        <SafeIcon icon={showPasscode ? FiEyeOff : FiEye} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Τουλάχιστον 6 χαρακτήρες
                  </p>
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={generateRandomPasscode}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Δημιουργία Τυχαίου Κωδικού
                  </button>
                </div>
              </div>
            </div>

            {/* Maintenance Message */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Μήνυμα Συντήρησης</h3>
              <div>
                <label htmlFor="maintenance_message" className="block text-sm font-medium text-gray-700 mb-1">
                  Κείμενο που θα εμφανίζεται στους επισκέπτες
                </label>
                <textarea
                  id="maintenance_message"
                  name="maintenance_message"
                  rows={3}
                  value={formData.maintenance_message}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Εισάγετε το μήνυμα που θα βλέπουν οι επισκέπτες..."
                />
              </div>
            </div>

            {/* Preview */}
            {formData.maintenance_mode && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">Προεπισκόπηση</h4>
                <div className="bg-white p-4 rounded border text-center">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <SafeIcon icon={FiSettings} className="w-4 h-4 text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Υπό Συντήρηση</h3>
                  <p className="text-sm text-gray-600">{formData.maintenance_message}</p>
                </div>
              </div>
            )}

            {/* Warning */}
            {formData.maintenance_mode && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800 mb-1">Προειδοποίηση</h4>
                    <p className="text-sm text-red-700">
                      Όταν ενεργοποιήσετε τη λειτουργία συντήρησης, όλοι οι επισκέπτες θα βλέπουν μόνο τη σελίδα συντήρησης. 
                      Φυλάξτε τον κωδικό πρόσβασης σε ασφαλές μέρος: <strong>{formData.maintenance_passcode}</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSettings;